import type { TFunction } from 'i18next'
import { useEffect, useRef } from 'react'
import { Control, useWatch, UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ContainerGroupField } from '@/components/common'
import {
    InputControl,
    SelectControl,
    DatePickerControl,
    AutocompleteControl
} from '@/components/form-controls'
import { useIcfes, useAuthHook } from '@/hooks'
import { EducationFormData } from '@/types/forms/educationForm'
import { IcfesFormField, IcfesType, AspirantIcfesExam } from '@/types/icfes'
import { inputValidations } from '@/utils'

interface IcfesControlProps {
    control: Control<EducationFormData>
    setValue: UseFormSetValue<EducationFormData>
    text: string
    watchIcfes: boolean | undefined
}

// ---- Helpers (fuera del componente para reducir líneas) ----
const SPECIAL_KEYS = ['SNP', 'NUMERO_REGISTRO', 'REGISTRO']

// Funciones helper para validar fechas según tipo de examen
function validateDateRange(
    examDate: Date,
    startDate: string,
    endDate: string,
    errorMessage: string
): string | true {
    const start = new Date(startDate)
    const end = new Date(endDate)

    // Ajustar las fechas para comparar solo el día (sin hora)
    const examDateOnly = new Date(
        examDate.getFullYear(),
        examDate.getMonth(),
        examDate.getDate()
    )
    const startOnly = new Date(
        start.getFullYear(),
        start.getMonth(),
        start.getDate()
    )
    const endOnly = new Date(end.getFullYear(), end.getMonth(), end.getDate())

    return examDateOnly >= startOnly && examDateOnly <= endOnly
        ? true
        : errorMessage
}

function validateDateFrom(
    examDate: Date,
    startDate: string,
    errorMessage: string
): string | true {
    const start = new Date(startDate)

    // Ajustar las fechas para comparar solo el día (sin hora)
    const examDateOnly = new Date(
        examDate.getFullYear(),
        examDate.getMonth(),
        examDate.getDate()
    )
    const startOnly = new Date(
        start.getFullYear(),
        start.getMonth(),
        start.getDate()
    )

    return examDateOnly >= startOnly ? true : errorMessage
}

// Función helper para parsear fechas en diferentes formatos
function parseSpanishDate(dateValue: string): Date | null {
    try {
        // Formato español completo: "13 de septiembre de 2019"
        if (dateValue.includes('de')) {
            const months = {
                enero: '01',
                febrero: '02',
                marzo: '03',
                abril: '04',
                mayo: '05',
                junio: '06',
                julio: '07',
                agosto: '08',
                septiembre: '09',
                octubre: '10',
                noviembre: '11',
                diciembre: '12'
            }

            const parts = dateValue.split(' de ')

            if (parts.length !== 3) {
                return new Date(dateValue)
            }

            const day = parts[0].padStart(2, '0')
            const month = months[parts[1] as keyof typeof months]
            const year = parts[2]

            if (!month) {
                return null
            }

            return new Date(`${year}-${month}-${day}`)
        }

        // Formato DD/MM/YYYY: "13/09/2019"
        if (dateValue.includes('/')) {
            const parts = dateValue.split('/')

            if (parts.length !== 3) {
                return new Date(dateValue)
            }

            const day = parts[0].padStart(2, '0')
            const month = parts[1].padStart(2, '0')
            const year = parts[2]

            return new Date(`${year}-${month}-${day}`)
        }

        // Formato ISO o estándar
        return new Date(dateValue)
    } catch {
        return null
    }
}

function validateExamDate(
    dateValue: string,
    examType: string | undefined
): string | true {
    if (!dateValue || !examType) {
        return true
    }

    const examDate = parseSpanishDate(dateValue)

    if (!examDate || isNaN(examDate.getTime())) {
        return 'Fecha inválida'
    }

    switch (examType) {
        case '06': // Examen extranjero - acepta cualquier fecha
            return true

        case '01': // Icfes 1999 hacia atrás
            return examDate.getFullYear() <= 1999
                ? true
                : 'La fecha debe ser del año 1999 o anterior'

        case '02': // Icfes 2000-01 al 2003-02
            return validateDateRange(
                examDate,
                '2000-01-01',
                '2003-12-31',
                'La fecha debe estar entre el 1 de enero de 2000 y el 31 de diciembre de 2003'
            )

        case '03': // Icfes 2004-01 al 2014-01
            return validateDateRange(
                examDate,
                '2004-01-01',
                '2014-06-30',
                'La fecha debe estar entre el 1 de enero de 2004 y el 30 de junio de 2014'
            )

        case '04': // Icfes 2014-02 al 2016-01
            return validateDateRange(
                examDate,
                '2014-07-01',
                '2016-06-30',
                'La fecha debe estar entre el 1 de julio de 2014 y el 30 de junio de 2016'
            )

        case '05': // Icfes 2016-02 en adelante
            return validateDateFrom(
                examDate,
                '2016-07-01',
                'La fecha debe ser del 1 de julio de 2016 en adelante'
            )

        default:
            return true
    }
}

const DATE_KEYS = ['FECHA_ICFES', 'FECHA_EXAMEN', 'FECHA']

// Mapeo de códigos técnicos a nombres legibles
const ICFES_FIELD_NAMES: Record<string, string> = {
    LECTURA_CRI: 'LECTURA CRITICA',
    CIEN_SOCI: 'CIENCIAS SOCIALES',
    SOCI_CIUDA: 'SOCIALES Y CIUDADANAS',
    MATEMATICAS: 'Matemáticas',
    CIEN_NATURALES: 'CIENCIAS NATURALES',
    INGLES: 'Inglés',
    COMPETENCIAS_CIUDADANAS: 'COMPETENCIAS CIUDADANAS',
    PUNTAJE_GLOBAL: 'PUNTAJE GLOBAL',
    PUESTO: 'PUESTO',
    PERCENTIL: 'PERCENTIL',
    FECHA_ICFES: 'FECHA DEL EXAMEN',
    NUMERO_REGISTRO: 'NUMERO DE REGISTRO',
    SNP: 'SNP'
}

// Función para obtener el nombre legible de un campo
function getReadableFieldName(fieldName: string, displayName?: string): string {
    // Si ya hay un displayName legible, usarlo
    if (
        displayName &&
        displayName !== '[object Object]' &&
        !displayName.includes('_')
    ) {
        return displayName
    }

    // Buscar en el mapeo por el displayName (no fieldName)
    if (displayName && displayName !== '[object Object]') {
        const upperDisplayName = displayName.toUpperCase()

        if (ICFES_FIELD_NAMES[upperDisplayName]) {
            return ICFES_FIELD_NAMES[upperDisplayName]
        }
    }

    // Si no está en el mapeo, intentar convertir el displayName
    if (displayName && displayName !== '[object Object]') {
        return displayName
            .split('_')
            .map(
                (word) =>
                    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            )
            .join(' ')
    }

    // Fallback al fieldName si no hay displayName
    return fieldName
        .split('_')
        .map(
            (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(' ')
}

// ---- Helpers de tipado de input (reducen complejidad en el render) ----
const isDateField = (field: IcfesFormField): boolean =>
    field.fieldName.toUpperCase().includes('FECHA') ||
    (field.fieldType || '').toLowerCase() === 'date'

const isNumericField = (field: IcfesFormField): boolean =>
    ['number', 'decimal'].includes((field.fieldType || '').toLowerCase())

const getInputTypeForField = (field: IcfesFormField): 'text' | 'number' =>
    isDateField(field) ? 'text' : isNumericField(field) ? 'number' : 'text'

const getAllowedPatternForField = (field: IcfesFormField) =>
    isDateField(field)
        ? inputValidations.dateSlashPattern
        : isNumericField(field)
          ? /^[0-9]{0,3}$/
          : undefined

// Normaliza texto (sin tildes/minúsculas) para comparaciones robustas
const normalize = (v?: string): string =>
    (v || '')
        .toString()
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase()
        .trim()

// ---- Helpers de preload (fuera del componente) ----
function pickExam(
    exams: AspirantIcfesExam[] | null | undefined,
    selectedIcfesType: unknown,
    icfesTypes: IcfesType[]
): AspirantIcfesExam | null {
    const list = exams || []

    if (list.length === 0) {
        return null
    }

    // Si hay uno seleccionado, tratar de ubicarlo por id o por descripción
    if (selectedIcfesType) {
        const code = String(selectedIcfesType).padStart(2, '0')
        const type = icfesTypes.find((t) => t.code === code)
        const desc = type?.description || String(selectedIcfesType)
        const id = parseInt(String(selectedIcfesType), 10)
        const byId = list.find((e) => Number(e.icfesTypeId) === id)

        if (byId) {
            return byId
        }
        const byDesc = list.find((e) =>
            normalize(e.icfesTypeName).includes(normalize(desc))
        )

        if (byDesc) {
            return byDesc
        }

        // Si el usuario seleccionó un tipo y no hay match, NO hacer fallback.
        return null
    }

    // Fallback: el primero disponible
    return list[0]
}

function parseExamDate(dateString?: string): number {
    if (!dateString) {
        return 0
    }
    // 'dd/MM/yyyy' o 'yyyy-MM-dd' o ISO
    if (dateString.includes('T')) {
        const t = Date.parse(dateString)

        return Number.isNaN(t) ? 0 : t
    }
    if (dateString.includes('/')) {
        const [day, month, year] = dateString.split('/').map((v) => Number(v))
        const d = new Date(year, month - 1, day)

        return d.getTime()
    }
    const t = Date.parse(dateString)

    return Number.isNaN(t) ? 0 : t
}

function getLatestExam(
    exams: AspirantIcfesExam[] | null | undefined
): AspirantIcfesExam | null {
    const list = exams || []

    if (list.length === 0) {
        return null
    }

    const getTs = (e: AspirantIcfesExam): number => {
        const byUpdated = e.updatedAt ? Date.parse(e.updatedAt) : 0

        if (!Number.isNaN(byUpdated) && byUpdated) {
            return byUpdated
        }
        const byCreated = e.createdAt ? Date.parse(e.createdAt) : 0

        if (!Number.isNaN(byCreated) && byCreated) {
            return byCreated
        }

        return parseExamDate(e.fechaIcfes)
    }

    return list.slice().sort((a, b) => getTs(b) - getTs(a))[0]
}

function setExamTypeToForm(
    exam: AspirantIcfesExam,
    setValue: any,
    icfesTypes: IcfesType[]
): void {
    const codeById = String(exam.icfesTypeId).padStart(2, '0')
    const codeExists = icfesTypes.some((t) => t.code === codeById)
    const codeByDesc =
        icfesTypes.find(
            (t) => normalize(t.description) === normalize(exam.icfesTypeName)
        )?.code || null
    const codeToSet = codeExists ? codeById : codeByDesc

    if (codeToSet) {
        setValue('icfesType', codeToSet as any)
    }
}

function setGlobalValuesToForm(exam: AspirantIcfesExam, setValue: any): void {
    setValue('icfesUniqueId', exam.numeroRegistro || '')
    setValue('icfesExamDate', exam.fechaIcfes || '')
    if (exam.puntajeGlobal) {
        setValue('icfesScores.PUNTAJE_GLOBAL' as any, exam.puntajeGlobal)
    }
    if (exam.puesto) {
        setValue('icfesScores.PUESTO' as any, exam.puesto)
    }
    if (exam.percentil) {
        setValue('icfesScores.PERCENTIL' as any, exam.percentil)
    }
}

function setAreasToForm(
    exam: AspirantIcfesExam,
    icfesFormFields: IcfesFormField[],
    setValue: any,
    idAspirant?: number
): void {
    exam.areas.forEach((a) => {
        // Caso especial: Si es ZZ_EXTRANJERO, guardarlo en foreignExam
        if (a.areaNombreSap.toUpperCase() === 'ZZ_EXTRANJERO') {
            // Para exámenes extranjeros, el código del examen está en el campo "puntaje"
            // no en "areaId" (que siempre es 20 para ZZ_EXTRANJERO)
            const foreignExamValue = a.puntaje
                ? String(a.puntaje).padStart(3, '0')
                : ''

            console.log(
                'Restaurando examen extranjero desde BD - puntaje:',
                a.puntaje,
                'formatted:',
                foreignExamValue
            )
            console.log('Area completa:', a)

            if (foreignExamValue) {
                setValue('foreignExam', foreignExamValue)

                // Actualizar sessionStorage con el valor de la BD (fuente de verdad)
                try {
                    const key = idAspirant
                        ? `foreignExam:${idAspirant}`
                        : 'foreignExam'

                    window.sessionStorage.setItem(key, foreignExamValue)
                } catch {}
            }

            return
        }

        const match = icfesFormFields.find(
            (f) => f.fieldName.toUpperCase() === a.areaNombreSap.toUpperCase()
        )

        if (match) {
            setValue(`icfesScores.${match.fieldName}` as any, a.puntaje || '')
        }
    })
}

function setGlobalDynamicFields(
    exam: AspirantIcfesExam,
    icfesFormFields: IcfesFormField[],
    setValue: any
): void {
    if (exam.puntajeGlobal) {
        // Buscar primero un campo que explícitamente sea GLOBAL
        let pgField = icfesFormFields.find((f) => {
            const n = f.fieldName.toUpperCase()

            return n.includes('GLOBAL') && !n.includes('PERCENTIL')
        })

        // Si no hay un campo llamado GLOBAL, intentar con PUNTAJE (excluyendo PERCENTIL)
        if (!pgField) {
            pgField = icfesFormFields.find((f) => {
                const n = f.fieldName.toUpperCase()

                return n.includes('PUNTAJE') && !n.includes('PERCENTIL')
            })
        }

        if (pgField) {
            setValue(
                `icfesScores.${pgField.fieldName}` as any,
                exam.puntajeGlobal
            )
        }
    }
    if (exam.puesto) {
        const pField = icfesFormFields.find((f) =>
            f.fieldName.toUpperCase().includes('PUESTO')
        )

        if (pField) {
            setValue(`icfesScores.${pField.fieldName}` as any, exam.puesto)
        }
    }
    if (exam.percentil) {
        const prField = icfesFormFields.find((f) =>
            f.fieldName.toUpperCase().includes('PERCENTIL')
        )

        if (prField) {
            setValue(`icfesScores.${prField.fieldName}` as any, exam.percentil)
        }
    }
}

function applyExamToForm(
    exam: AspirantIcfesExam,
    icfesFormFields: IcfesFormField[],
    setValue: any,
    icfesTypes: IcfesType[],
    allowSetType: boolean,
    idAspirant?: number,
    getForeignExams?: () => Promise<void>
): void {
    if (allowSetType) {
        setExamTypeToForm(exam, setValue, icfesTypes)
    }
    setGlobalValuesToForm(exam, setValue)
    setAreasToForm(exam, icfesFormFields, setValue, idAspirant)
    setGlobalDynamicFields(exam, icfesFormFields, setValue)

    // Si el examen es de tipo extranjero (06), cargar la lista de exámenes extranjeros
    const examTypeCode = String(exam.tipoIcfesCode).padStart(2, '0')

    if (examTypeCode === '06' && getForeignExams) {
        console.log('Cargando exámenes extranjeros porque el tipo es 06')
        void getForeignExams()
    }
}

function filterIcfesFields(fields: IcfesFormField[]): IcfesFormField[] {
    const seen = new Set<string>()

    return fields.filter((field) => {
        const fieldName = field.fieldName.toUpperCase()

        if (
            SPECIAL_KEYS.some((key) => fieldName.includes(key)) ||
            DATE_KEYS.some((key) => fieldName.includes(key))
        ) {
            return false
        }
        const fieldKey = field.fieldName.toUpperCase()

        if (seen.has(fieldKey)) {
            return false
        }
        if (
            !field.fieldName ||
            !field.displayName ||
            field.displayName === '[object Object]'
        ) {
            return false
        }
        seen.add(fieldKey)

        return true
    })
}

function resolveFieldValue(
    field: IcfesFormField,
    icfesUniqueId?: string,
    icfesExamDate?: string
): string {
    const fieldName = field.fieldName.toUpperCase()

    if (SPECIAL_KEYS.some((key) => fieldName.includes(key))) {
        return icfesUniqueId || field.defaultValue || ''
    }
    if (DATE_KEYS.some((key) => fieldName.includes(key))) {
        return icfesExamDate || field.defaultValue || ''
    }

    return (
        field.defaultValue ||
        (field.fieldType?.toLowerCase() === 'number' ? '0' : '')
    )
}

function buildFieldRules(
    t: TFunction,
    field: IcfesFormField,
    cleanLabel: string
) {
    return {
        required: field.required ? `${cleanLabel} es requerido` : false,
        maxLength:
            field.maxLength > 0
                ? {
                      value: field.maxLength,
                      message: t('forms.validation.maxLength', {
                          max: field.maxLength
                      })
                  }
                : undefined,
        min: field.minValue
            ? {
                  value: parseFloat(field.minValue),
                  message: t('forms.validation.minValue', {
                      min: field.minValue
                  })
              }
            : undefined,
        max: field.maxValue
            ? {
                  value: parseFloat(field.maxValue),
                  message: t('forms.validation.maxValue', {
                      max: field.maxValue
                  })
              }
            : undefined,
        validate:
            (field.fieldType || '').toLowerCase() === 'number'
                ? {
                      maxDigits: (value: unknown) => {
                          if (
                              value === undefined ||
                              value === null ||
                              value === ''
                          ) {
                              return true
                          }

                          const stringValue = String(value)
                          const numeric = Number(value)

                          if (Number.isNaN(numeric)) {
                              return `${cleanLabel} debe ser numérico`
                          }

                          // Restricción de máximo 3 dígitos
                          if (stringValue.length > 3) {
                              return `${cleanLabel} no puede tener más de 3 dígitos`
                          }

                          // Validaciones de rango si existen
                          const hasMin =
                              field.minValue !== undefined &&
                              field.minValue !== ''
                          const hasMax =
                              field.maxValue !== undefined &&
                              field.maxValue !== ''

                          if (hasMin && numeric < Number(field.minValue)) {
                              return `${cleanLabel} debe ser mayor o igual a ${field.minValue}`
                          }
                          if (hasMax && numeric > Number(field.maxValue)) {
                              return `${cleanLabel} debe ser menor o igual a ${field.maxValue}`
                          }

                          return true
                      }
                  }
                : undefined
    }
}

function createIcfesFieldRenderer(
    control: Control<EducationFormData>,
    t: TFunction,
    icfesUniqueId?: string,
    icfesExamDate?: string
) {
    return (field: IcfesFormField): JSX.Element => {
        const readableLabel = getReadableFieldName(
            field.displayName || field.fieldName,
            field.displayName
        )
        const labelToShow = readableLabel
        const value = resolveFieldValue(field, icfesUniqueId, icfesExamDate)
        const fieldRules = buildFieldRules(t, field, readableLabel)

        return (
            <div
                key={field.fieldName}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:border-green-300 hover:shadow-sm transition-all duration-200"
            >
                <div className="mb-3">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span
                            className="text-xs font-medium text-gray-700"
                            title={readableLabel}
                        >
                            {labelToShow}
                        </span>
                    </div>
                </div>
                <InputControl
                    name={`icfesScores.${field.fieldName}` as any}
                    control={control}
                    label=""
                    rules={fieldRules as any}
                    typeInput={getInputTypeForField(field)}
                    allowedPattern={getAllowedPatternForField(field)}
                    defaultValue={value}
                />
            </div>
        )
    }
}

// Componente para la información básica del examen
const ExamInfoSection = ({
    control,
    icfesTypes,
    loading,
    text,
    setValue,
    selectedIcfesType
}: {
    control: Control<EducationFormData>
    icfesTypes: any[]
    loading: boolean
    text: string
    setValue: UseFormSetValue<EducationFormData>
    selectedIcfesType: string | undefined
}) => {
    const { t } = useTranslation()

    return (
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <svg
                        className="w-4 h-4 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                    </svg>
                </div>
                <h4 className="text-lg font-semibold text-gray-800">
                    Información del Examen
                </h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Selector de tipo de ICFES */}
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-sm font-medium text-gray-600">
                            Tipo de Examen
                        </span>
                    </div>
                    <SelectControl
                        name="icfesType"
                        control={control}
                        label=""
                        options={icfesTypes
                            .filter((type) => type.description.trim() !== '')
                            .map((type) => ({
                                value: type.code,
                                label: type.description
                            }))}
                        rules={{
                            required: t(`${text}.required.icfesType`)
                        }}
                        placeholder={
                            loading
                                ? 'Cargando...'
                                : icfesTypes.length === 0
                                  ? 'No hay opciones disponibles'
                                  : t('forms.select.placeholder')
                        }
                        isLoading={loading}
                    />
                </div>

                {selectedIcfesType !== '06' && (
                    <>
                        {/* Número de registro ICFES */}
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                <span className="text-sm font-medium text-gray-600">
                                    Número de Registro
                                </span>
                            </div>
                            <InputControl
                                name="icfesUniqueId"
                                allowedPattern={
                                    inputValidations.alphaNumericCharacters
                                }
                                typeInput="text"
                                control={control}
                                label=""
                                rules={{
                                    required:
                                        selectedIcfesType !== '06'
                                            ? t(`${text}.required.icfesUniqueId`)
                                            : false
                                }}
                            />
                        </div>

                        {/* Fecha de presentación del examen */}
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                <span className="text-sm font-medium text-gray-600">
                                    Fecha de Presentación
                                </span>
                            </div>
                            <DatePickerControl
                                name="icfesExamDate"
                                control={control}
                                label={`${text}.icfesExamDate`}
                                rules={{
                                    required:
                                        selectedIcfesType !== '06'
                                            ? t(
                                                  `${text}.required.icfesExamDate`
                                              )
                                            : false,
                                    validate: (value: string) =>
                                        validateExamDate(
                                            value,
                                            selectedIcfesType
                                        )
                                }}
                                onCustomChange={(val) =>
                                    setValue('icfesExamDate', val)
                                }
                                hideLabel
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}

// Componente para el selector de exámenes extranjeros
const ForeignExamSection = ({
    control,
    foreignExams,
    loading,
    text
}: {
    control: Control<EducationFormData>
    foreignExams: any[]
    loading: boolean
    text: string
}) => {
    const { t } = useTranslation()

    console.log(
        'ForeignExamSection renderizado - foreignExams:',
        foreignExams,
        'loading:',
        loading
    )

    return (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <svg
                        className="w-4 h-4 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9"
                        />
                    </svg>
                </div>
                <h4 className="text-lg font-semibold text-gray-800">
                    Examen Extranjero
                </h4>
            </div>

            <div className="space-y-2">
                <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm font-medium text-gray-600">
                        Selecciona el examen extranjero
                    </span>
                </div>
                <AutocompleteControl
                    name="foreignExam"
                    control={control}
                    label=""
                    options={foreignExams
                        .filter(
                            (exam) =>
                                exam.value !== '000' && exam.label.trim() !== ''
                        )
                        .map((exam) => ({
                            value: exam.value,
                            label: exam.label
                        }))}
                    rules={{
                        required: 'Debes seleccionar un examen extranjero'
                    }}
                    placeholder={
                        loading
                            ? 'Cargando...'
                            : foreignExams.length === 0
                              ? 'No hay opciones disponibles'
                              : 'Busca y selecciona un examen'
                    }
                    isLoading={loading}
                    menuTrigger="input"
                    allowsCustomValue={false}
                    isClearable={true}
                />
            </div>
        </div>
    )
}

// Componente para los campos dinámicos de puntajes
const ExamScoresSection = ({
    control,
    icfesFormFields,
    getFilteredFields,
    renderIcfesField,
    text
}: {
    control: Control<EducationFormData>
    icfesFormFields: IcfesFormField[]
    getFilteredFields: (fields: IcfesFormField[]) => IcfesFormField[]
    renderIcfesField: (field: IcfesFormField) => JSX.Element
    text: string
}) => {
    const { t } = useTranslation()

    return (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <svg
                        className="w-4 h-4 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                        />
                    </svg>
                </div>
                <h4 className="text-lg font-semibold text-gray-800">
                    {t(`${text}.examScores`)}
                </h4>
            </div>

            <div className="w-full grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-6 justify-items-center">
                {getFilteredFields(icfesFormFields).map(renderIcfesField)}
            </div>

            {getFilteredFields(icfesFormFields).length === 0 && (
                <div className="text-center py-8">
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <svg
                            className="w-6 h-6 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                        </svg>
                    </div>
                    <p className="text-gray-500 text-sm">
                        No hay campos de puntajes disponibles para este tipo de
                        examen.
                    </p>
                </div>
            )}
        </div>
    )
}

// eslint-disable-next-line max-lines-per-function
export const IcfesControl = ({
    control,
    setValue,
    text,
    watchIcfes
}: IcfesControlProps): JSX.Element => {
    const { t } = useTranslation()
    const {
        icfesTypes,
        icfesFormFields,
        foreignExams,
        loading,
        error,
        getIcfesTypes,
        getIcfesFormFields,
        getForeignExams,
        getAspirantIcfes
    } = useIcfes()

    const { idAspirant } = useAuthHook()

    const selectedIcfesType = useWatch({
        control,
        name: 'icfesType'
    })

    const icfesUniqueId = useWatch({
        control,
        name: 'icfesUniqueId'
    })

    const icfesExamDate = useWatch({
        control,
        name: 'icfesExamDate'
    })

    const foreignExam = useWatch({
        control,
        name: 'foreignExam'
    })

    // Observa el objeto de puntajes para inicialización de claves
    const icfesScores = useWatch({
        control,
        name: 'icfesScores'
    }) as Record<string, unknown> | undefined

    // Cargar tipos de ICFES al montar el componente
    useEffect(() => {
        if (watchIcfes) {
            getIcfesTypes()
        }
    }, [watchIcfes])

    // Cargar lista de exámenes extranjeros cuando el tipo sea "06"
    useEffect(() => {
        console.log(
            'useEffect triggered - watchIcfes:',
            watchIcfes,
            'selectedIcfesType:',
            selectedIcfesType
        )
        if (watchIcfes && selectedIcfesType === '06') {
            console.log('Cargando exámenes extranjeros...')
            getForeignExams()
            // Limpiar puntajes cuando se selecciona examen extranjero
            setValue('icfesScores' as any, {} as any)
        } else if (
            watchIcfes &&
            selectedIcfesType &&
            selectedIcfesType !== '06'
        ) {
            // Limpiar examen extranjero cuando se selecciona otro tipo
            setValue('foreignExam', '' as any)
        }
    }, [watchIcfes, selectedIcfesType])

    // useEffect adicional para cargar exámenes extranjeros cuando el componente se monta con tipo "06" ya seleccionado
    useEffect(() => {
        console.log(
            'useEffect inicial - watchIcfes:',
            watchIcfes,
            'selectedIcfesType:',
            selectedIcfesType,
            'foreignExams.length:',
            foreignExams.length
        )
        if (
            watchIcfes &&
            selectedIcfesType === '06' &&
            foreignExams.length === 0 &&
            !loading
        ) {
            console.log('Cargando exámenes extranjeros en montaje inicial...')
            getForeignExams()
        }
    }, [
        watchIcfes,
        selectedIcfesType,
        foreignExams.length,
        loading,
        getForeignExams
    ])

    // Persistir selección de tipo entre pasos (por aspirante)
    useEffect(() => {
        const key = idAspirant ? `icfesType:${idAspirant}` : 'icfesType'

        if (selectedIcfesType) {
            try {
                window.sessionStorage.setItem(key, String(selectedIcfesType))
            } catch {}
        }
    }, [selectedIcfesType, idAspirant])

    // Persistir selección de examen extranjero
    useEffect(() => {
        const key = idAspirant ? `foreignExam:${idAspirant}` : 'foreignExam'

        if (foreignExam) {
            try {
                window.sessionStorage.setItem(key, String(foreignExam))
            } catch {}
        } else {
            // Limpiar del sessionStorage cuando se borra
            try {
                window.sessionStorage.removeItem(key)
            } catch {}
        }
    }, [foreignExam, idAspirant])

    // Restaurar selección guardada si el formulario viene vacío
    useEffect(() => {
        if (!watchIcfes) {
            return
        }

        const hasSelected = !!selectedIcfesType

        if (hasSelected) {
            return
        }

        try {
            const key = idAspirant ? `icfesType:${idAspirant}` : 'icfesType'
            const stored = window.sessionStorage.getItem(key)

            if (stored) {
                setValue('icfesType', stored as any)
            }
        } catch {}
    }, [watchIcfes, icfesTypes, idAspirant, selectedIcfesType, setValue])

    // NO restaurar desde sessionStorage - solo desde BD
    // El sessionStorage solo se usa para mantener el valor durante la navegación en la misma sesión
    // pero la BD es la fuente de verdad

    // Cargar campos de formulario cuando cambie el tipo de ICFES
    useEffect(() => {
        if (selectedIcfesType && watchIcfes) {
            getIcfesFormFields(selectedIcfesType)
        }
    }, [selectedIcfesType, watchIcfes])

    // Al cambiar de tipo, intentar cargar resultados de ese tipo; si no existen, limpiar
    useEffect(() => {
        if (!watchIcfes || !idAspirant || !selectedIcfesType) {
            return
        }

        // Esperar a que estén los campos del template para mapear áreas
        if (!icfesFormFields.length) {
            return
        }

        const loadExamBySelectedType = async (): Promise<void> => {
            const exams = await getAspirantIcfes(idAspirant)
            const exam = pickExam(exams, selectedIcfesType, icfesTypes)

            if (exam) {
                // Cargar valores del examen para este tipo, sin cambiar el tipo seleccionado
                applyExamToForm(
                    exam,
                    icfesFormFields,
                    setValue,
                    icfesTypes,
                    false,
                    idAspirant,
                    getForeignExams
                )
            } else {
                // No hay examen de ese tipo: limpiar solo los puntajes, preservar número de registro y fecha si tienen valores
                setValue('icfesScores' as any, {} as any)

                // Solo limpiar icfesUniqueId si está vacío
                if (!icfesUniqueId) {
                    setValue('icfesUniqueId', '')
                }

                // Solo limpiar icfesExamDate si está vacía
                if (!icfesExamDate) {
                    setValue('icfesExamDate', '')
                }
            }
        }

        void loadExamBySelectedType()
        // setValue y getAspirantIcfes son estables para nuestro caso; excluirlos evita loops
    }, [watchIcfes, idAspirant, selectedIcfesType, icfesFormFields, icfesTypes])

    // Preload de resultados por aspirante (usa helpers para reducir líneas)
    // Evita sobreescribir la selección del usuario: sólo se ejecuta una vez
    const didPreloadRef = useRef(false)

    useEffect(() => {
        if (!watchIcfes || !idAspirant) {
            return
        }

        const loadExams = async (): Promise<void> => {
            if (didPreloadRef.current) {
                return
            }
            const exams = await getAspirantIcfes(idAspirant)
            // Siempre tomar el examen más reciente
            const exam = getLatestExam(exams)

            if (exam) {
                // Sólo fijar el tipo si el usuario aún no ha seleccionado uno
                const allowSetType = !selectedIcfesType

                applyExamToForm(
                    exam,
                    icfesFormFields,
                    setValue,
                    icfesTypes,
                    allowSetType,
                    idAspirant,
                    getForeignExams
                )
                didPreloadRef.current = true
            }
        }

        void loadExams()
        // selectedIcfesType deliberadamente fuera para no sobreescribir al cambiar
    }, [idAspirant, watchIcfes, icfesFormFields, icfesTypes])

    // Ya no persistimos selección: siempre se usa el examen más reciente
    // Sincronizar valores con campos especiales automáticamente
    useEffect(() => {
        if (!watchIcfes || !icfesFormFields.length) {
            return
        }

        icfesFormFields.forEach((field) => {
            const fieldName = field.fieldName.toUpperCase()

            const isIdKey = ['SNP', 'NUMERO_REGISTRO', 'REGISTRO'].some((key) =>
                fieldName.includes(key)
            )

            if (isIdKey) {
                if (icfesUniqueId) {
                    setValue(`icfesScores.${field.fieldName}`, icfesUniqueId)
                }
            }

            const isDateKey = ['FECHA_ICFES', 'FECHA_EXAMEN'].some((key) =>
                fieldName.includes(key)
            )

            if (isDateKey) {
                if (icfesExamDate) {
                    setValue(`icfesScores.${field.fieldName}`, icfesExamDate)
                }
            }
        })
    }, [icfesUniqueId, icfesExamDate, icfesFormFields, setValue, watchIcfes])

    const getFilteredFields = (fields: IcfesFormField[]) =>
        filterIcfesFields(fields)

    // Campos especiales que deben sincronizarse con valores principales
    const getFieldValue = (field: IcfesFormField) =>
        resolveFieldValue(field, icfesUniqueId, icfesExamDate)

    const getFieldRules = (field: IcfesFormField, cleanLabel: string) =>
        buildFieldRules(t, field, cleanLabel)

    const renderIcfesField = createIcfesFieldRenderer(
        control,
        t,
        icfesUniqueId,
        icfesExamDate
    )

    // Inicializar claves de icfesScores para todos los campos del template actual
    useEffect(() => {
        if (!watchIcfes || !selectedIcfesType || !icfesFormFields.length) {
            return
        }

        const fields = filterIcfesFields(icfesFormFields)

        fields.forEach((f) => {
            const key = f.fieldName
            const current = icfesScores ? (icfesScores as any)[key] : undefined

            if (current === undefined) {
                setValue(`icfesScores.${key}` as any, '' as any, {
                    shouldDirty: false,
                    shouldValidate: false
                })
            }
        })
    }, [watchIcfes, selectedIcfesType, icfesFormFields, icfesScores, setValue])

    return (
        <ContainerGroupField
            tittleGroup={t('forms.groups.icfes')}
            onlyOneField
        >
            {watchIcfes && (
                <div className="space-y-6">
                    <ExamInfoSection
                        control={control}
                        icfesTypes={icfesTypes}
                        loading={loading}
                        text={text}
                        setValue={setValue}
                        selectedIcfesType={selectedIcfesType}
                    />

                    {selectedIcfesType === '06' ? (
                        <ForeignExamSection
                            control={control}
                            foreignExams={foreignExams}
                            loading={loading}
                            text={text}
                        />
                    ) : (
                        selectedIcfesType &&
                        icfesFormFields.length > 0 && (
                            <ExamScoresSection
                                control={control}
                                icfesFormFields={icfesFormFields}
                                getFilteredFields={getFilteredFields}
                                renderIcfesField={renderIcfesField}
                                text={text}
                            />
                        )
                    )}

                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <div className="text-red-600 text-sm font-medium">
                                {error}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </ContainerGroupField>
    )
}
